/**
 * Панель заказа — создание и редактирование.
 *
 * Здесь живёт каскад врач → клиника → филиал → юрлицо → способ оплаты → цена
 * (§1, §3). Вся его арифметика вынесена в `@otc/shared` и покрыта тестами:
 * экран только вызывает `applyDoctorChange`, `applyClinicChange` и остальные,
 * но НЕ считает цену сам. Именно самостоятельный подсчёт в обработчике привёл
 * в демо к реальной ошибке — при смене клиники обновлялся способ оплаты, но
 * не пересчитывалась стоимость.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, ChevronDown, Mail, MailCheck, Trash2, Wallet, X, FileSpreadsheet, FileText } from 'lucide-react';
import {
  SLM_SUPPLIERS, defaultExpenseKop, type SlmSupplier,
  applyBranchChange,
  applyClinicChange,
  applyConstructionChange,
  applyConstructionQtyChange,
  applyDoctorChange,
  applyExtrasChange,
  isModelPrintBase,
  modelPrintExtraId,
  applyPayChange,
  catalogIndex,
  clinicsForDoctor,
  formatKop,
  letterDue,
  payOverride,
  priceOf,
  rublesToKop,
  todayIso,
  urgencyOf,
  type CascadeContext,
  type Clinic,
  type Doctor,
  type Jaw,
  type OrderCascadeState,
  type OrderExtra,
  type OrderStatus,
  type PayMethod,
  type Urgency,
} from '@otc/shared';
import type {
  ApiCategory,
  ApiClinic,
  ApiColor,
  ApiConstruction,
  ApiDoctor,
  ApiOrder,
} from '../api/queries.js';
import { Badge, Button, ConfirmInline, Field } from '../components/ui.js';
import { Swatch } from '../components/Swatch.js';
import { Messengers } from '../components/Messengers.js';
import { CardWindow, зажать, ОТСТУП, ШИРИНА_ДВА, type Окно } from '../components/CardWindow.js';
import { ActionPill, Pilule } from '../components/Pills.js';
import { DateField } from '../components/DateField.js';
import { подсказкаПоиска } from '../components/isNarrow.js';
import { useOutsideClose } from '../components/useOutsideClose.js';
import { STATUS_COLORS } from '../theme.js';

export interface OrderDraft {
  slmSupplier?: SlmSupplier;
  id: string;
  doctorId: string;
  clinicId: string;
  branchId: string;
  /** Челюсть — приписка к названию конструкции. Пусто — приписки нет. */
  jaw: Jaw;
  constructionId: string;
  /** Сколько одинаковых аппаратов в заказе. Обычно один. */
  constructionQty: number;
  colors: string[];
  extras: OrderExtra[];
  patient: string;
  received: string;
  /** `null` — срок ещё не согласован. */
  due: string | null;
  status: OrderStatus;
  urgency: Urgency;
  /*
   * Способ оплаты может быть ЕЩЁ НЕ ОПРЕДЕЛЁН — пустая строка.
   *
   * Он не свободный выбор, а свойство клиники: у одной работа по договору,
   * у другой наличными. Пока клиника не названа, брать его неоткуда, и
   * подставленное «По договору» было бы догадкой — а по нему считается цена.
   * В сохранённом заказе пусто не бывает: без способа оплаты карточка не
   * сохраняется, да и сервер такой заказ не примет.
   */
  pay: PayMethod | '';
  paid: boolean;
  costKop: number;
  expenseKop: number;
  notes: string;
  invoiceId: string | null;
  invoiceNumber: number | null;
}

/** Приведение форм API к доменным типам, которых ждёт `@otc/shared`. */
function toDomainClinics(clinics: ApiClinic[]): Clinic[] {
  return clinics.map((c) => ({
    id: c.id,
    name: c.name,
    version: c.version,
    branches: c.branches,
    legals: c.legals,
  })) as unknown as Clinic[];
}

function toDomainDoctors(doctors: ApiDoctor[]): Doctor[] {
  return doctors as unknown as Doctor[];
}

/**
 * Новый заказ: врач и клиника подставлены, ИЗДЕЛИЕ — НЕТ.
 *
 * Раньше сюда подставлялась первая базовая конструкция справочника — по
 * алфавиту это «Активатор Klammt». Заказ открывался с уже выбранным аппаратом
 * и его ценой, хотя никто ничего не выбирал: достаточно было не заметить
 * подставленное — и в работу уходил чужой аппарат с чужой стоимостью.
 * Пустое поле такой ошибки не допускает: оно прямо говорит «Выбрать
 * конструкцию», а цена остаётся нулевой, пока изделие не названо.
 *
 * По той же причине пуст и ВРАЧ. Прежде подставлялся первый по алфавиту, и
 * незамеченный заказ уходил на чужого врача — а по врачу считается и счёт,
 * и зарплата. Клиника подставится сама, как только врача выберут: каскад
 * возьмёт ту, где он работает.
 *
 * Допы тоже пусты: печать модели появляется, когда выберут основную
 * конструкцию, — см. `chooseConstruction` в карточке.
 */
export function blankOrder(clinics: ApiClinic[], doctors: ApiDoctor[]): OrderDraft | null {
  // Врачей нет вовсе — заводить заказ не на кого.
  if (doctors.length === 0 || clinics.length === 0) return null;

  return {
    id: '',
    doctorId: '',
    clinicId: '',
    branchId: '',
    jaw: '',
    constructionId: '',
    constructionQty: 1,
    colors: [],
    extras: [],
    patient: '',
    received: todayIso(),
    due: null,
    status: 'В очереди',
    urgency: 'Авто',
    // Пусто: способ оплаты придёт вместе с клиникой выбранного врача.
    pay: '',
    paid: false,
    // Ноль — это «ещё не посчитано», а не бесплатно: цена появится вместе
    // с изделием, тем же пересчётом, что и при любой другой смене конструкции.
    costKop: 0,
    expenseKop: 0,
    notes: '',
    invoiceId: null,
    invoiceNumber: null,
  };
}

/** Как назвать письмо в подписи кнопки — по статусу заказа. */
function letterLabel(status: string): string {
  return letterDue(status) === 'ACCEPTED' ? 'о принятии заказа' : 'о готовности';
}

export function OrderPanel({
  order,
  isNew,
  clinics,
  doctors,
  constructions,
  colors,
  categories,
  onSave,
  onDelete,
  onInvoice,
  onOpenInvoice,
  onReport,
  invoiceHint,
  onClose,
  onDraftChange,
  letterState,
  onSendLetter,
}: {
  order: OrderDraft;
  isNew: boolean;
  clinics: ApiClinic[];
  doctors: ApiDoctor[];
  constructions: ApiConstruction[];
  colors: ApiColor[];
  categories: ApiCategory[];
  /**
   * Применить правки.
   *
   * `держатьОткрытой` — не закрывать карточку после сохранения. Нужно
   * кнопкам документов: они сперва применяют правки, а потом открывают счёт
   * или отчёт поверх карточки. Без этого карточка закрывалась вместе с
   * сохранением, и, закрыв счёт, человек оказывался в журнале — заказ,
   * с которым он работал, исчезал из-под рук.
   */
  onSave: (draft: OrderDraft, держатьОткрытой?: boolean) => void;
  onDelete?: () => void;
  /**
   * Выставить счёт и акт по этому заказу. Не передан — заказ ещё не
   * сохранён, и выставлять нечего: сервер о нём не знает.
   */
  onInvoice?: () => void;
  /** Открыть счёт, в который заказ уже включён. */
  onOpenInvoice?: () => void;
  /** Собрать отчёт по этому заказу. */
  onReport?: () => void;
  /** Почему счёт выставить нельзя. Пусто — можно. */
  invoiceHint?: string | null;
  onClose: () => void;
  /**
   * Что сейчас с письмом врачу. `null` — заказа ещё нет на сервере, писать
   * не о чем: письмо ссылается на заказ, которого нет.
   */
  letterState: {
    /** Статус, СОХРАНЁННЫЙ на сервере — не тот, что сейчас в полях. */
    savedStatus: string;
    /** Какие письма по заказу уже уходили. */
    sentKinds: ('ACCEPTED' | 'READY')[];
    /** Почему писать нельзя вообще. `null` — можно. */
    blocked: string | null;
  } | null;
  /** Текущее состояние правок — журналу, чтобы не потерять их при переходе. */
  onDraftChange?: (draft: OrderDraft) => void;
  onSendLetter: (kind: 'ACCEPTED' | 'READY') => void;
}) {
  const [local, setLocal] = useState<OrderDraft>(order);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    onDraftChange?.(local);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  const domainClinics = useMemo(() => toDomainClinics(clinics), [clinics]);
  const domainDoctors = useMemo(() => toDomainDoctors(doctors), [doctors]);
  const catalog = useMemo(() => catalogIndex(constructions), [constructions]);

  const ctx: CascadeContext = { clinics: domainClinics, doctors: domainDoctors, catalog };

  /** Применение операции каскада из `@otc/shared` к локальному черновику. */
  const applyCascade = (next: OrderCascadeState & { colors?: string[] }) => {
    setLocal((current) => ({
      ...current,
      doctorId: next.doctorId,
      clinicId: next.clinicId,
      branchId: next.branchId,
      constructionId: next.constructionId,
      constructionQty: next.constructionQty,
      slmSupplier: next.slmSupplier ?? current.slmSupplier ?? 'M',
      extras: next.extras,
      /*
       * Способ оплаты остаётся ПУСТЫМ, пока не выбрана клиника: взять его
       * всё равно неоткуда. Как только клиника появилась, каскад подставляет
       * её значение — и дальше поле ведёт себя как обычно.
       *
       * Выбор РУКАМИ сюда не попадает: он идёт через `choosePay` ниже. Иначе
       * эта же строка его и съедала — у частного заказа и у врача без клиники
       * способ оплаты нельзя было поставить вовсе: нажимаешь «Наличные», а в
       * поле остаётся «Выбрать способ оплаты».
       */
      pay: next.clinicId === '' && current.pay === '' ? '' : next.pay,
      costKop: next.costKop,
      expenseKop: next.expenseKop,
      ...(next.colors ? { colors: next.colors } : {}),
    }));
  };

  /**
   * Способ оплаты, выбранный РУКАМИ.
   *
   * Мимо `applyCascade`: тот бережёт пустоту, пока клиника не названа, и
   * ручной выбор в этом случае терялся. Цена пересчитывается тем же
   * `applyPayChange` — наличные и договор стоят по-разному.
   */
  const choosePay = (pay: PayMethod) => {
    const next = applyPayChange(cascadeState(), pay, ctx);
    setLocal((current) => ({ ...current, pay, costKop: next.costKop }));
  };

  const cascadeState = (): OrderCascadeState => ({
    doctorId: local.doctorId,
    clinicId: local.clinicId,
    branchId: local.branchId,
    constructionId: local.constructionId,
    constructionQty: local.constructionQty,
    slmSupplier: local.slmSupplier ?? 'M',
    extras: local.extras,
    // Для расчёта нужен конкретный способ; пока его нет, считаем по договору —
    // цена всё равно появится вместе с клиникой и пересчитается.
    pay: local.pay || 'По договору',
    costKop: local.costKop,
    expenseKop: local.expenseKop,
  });

  const doctor = doctors.find((d) => d.id === local.doctorId);
  const availableClinics = useMemo(
    () => clinicsForDoctor(domainClinics, doctor as unknown as Doctor | undefined),
    [domainClinics, doctor],
  );
  const clinic = clinics.find((c) => c.id === local.clinicId);
  const construction = constructions.find((k) => k.id === local.constructionId);

  /*
   * Способ оплаты ДЛЯ ПОКАЗА ЦЕН. Пока он не выбран, цены в списках надо
   * чем-то считать: берём договорные — они же подставятся у большинства
   * клиник, а при выборе всё пересчитается по-настоящему.
   */
  const payДляЦен: PayMethod = local.pay || 'По договору';

  const override = payOverride(domainClinics, { ...local, pay: payДляЦен });
  const urgency = urgencyOf(local, todayIso());

  /**
   * Позиции справочника, разложенные по категориям — как в разделе
   * «Конструкции». Плоский алфавитный список из 39 аппаратов заставлял
   * искать нужный перебором: техник помнит, что каппа лежит в «Штампованных
   * изделиях», а не то, на какую букву она начинается.
   *
   * Позиции без известной категории попадают в отдельную группу, а не
   * пропадают из списка.
   */
  const groupByCategory = (type: ApiConstruction['type'], kind: 'base' | 'extra') => {
    const items = constructions.filter((k) => k.type === type);
    const cats = categories
      .filter((c) => c.kind === kind)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'));

    const groups = cats
      .map((category) => ({
        name: category.name,
        items: items.filter((k) => k.categoryId === category.id),
      }))
      .filter((group) => group.items.length > 0);

    const known = new Set(cats.map((c) => c.id));
    const orphans = items.filter((k) => !known.has(k.categoryId));
    if (orphans.length > 0) groups.push({ name: 'Без категории', items: orphans });

    return groups;
  };

  const baseGroups = useMemo(
    () => groupByCategory('Базовая конструкция', 'base'),
    [constructions, categories],
  );
  const extraGroups = useMemo(
    () => groupByCategory('Дополнительный элемент', 'extra'),
    [constructions, categories],
  );

  const profitKop = local.costKop - local.expenseKop;

  /*
   * ПЕЧАТЬ МОДЕЛИ в новом заказе появляется ВМЕСТЕ С ОСНОВНОЙ КОНСТРУКЦИЕЙ.
   *
   * Большинство работ приходит сканом, а скан заканчивается печатью модели,
   * поэтому при выборе конструкции доп ×1 ставится сам. Пустая карточка до
   * выбора его не показывает: цены без изделия ещё нет, и одинокий доп
   * выглядел бы заказом на одну печать.
   *
   * Правила (`model-print.ts`):
   *   - основа сама печать модели — доп убирается, иначе печать дважды;
   *   - человек убрал доп или поменял количество — дальше программа его не трогает;
   *   - в уже заведённом заказе доп сам не добавляется: заказ живёт своей жизнью.
   */
  const печатьТронута = useRef(false);
  const chooseConstruction = (id: string) => {
    const next = applyConstructionChange(cascadeState(), id, ctx);
    const печать = modelPrintExtraId(constructions);
    if (!печать) return applyCascade(next);

    const есть = next.extras.some((e) => e.id === печать);
    if (isModelPrintBase(constructions.find((k) => k.id === id))) {
      if (!есть) return applyCascade(next);
      return applyCascade({ ...applyExtrasChange(next, next.extras.filter((e) => e.id !== печать), ctx), colors: next.colors });
    }
    if (isNew && id && !есть && !печатьТронута.current) {
      /*
       * На ОБЕ челюсти печатают две модели — по одной на каждую.
       *
       * Поле челюсти в карточке стоит выше конструкции, и заполняют её
       * обычно первой: печать подставлялась одной штукой, а вторую
       * приходилось дописывать руками — ровно то, от чего подстановка
       * и избавляет.
       */
      const сколько = local.jaw === 'Обе челюсти' ? 2 : 1;
      return applyCascade({
        ...applyExtrasChange(next, [...next.extras, { id: печать, qty: сколько }], ctx),
        colors: next.colors,
      });
    }
    applyCascade(next);
  };

  /**
   * Смена челюсти.
   *
   * «Обе челюсти» — это ДВА аппарата, верхний и нижний, а не один на двух
   * челюстях. Поэтому количество конструкции ставится двойкой, и печать
   * модели, если она в заказе, тоже удваивается: моделей печатают две.
   *
   * Возврат к одной челюсти удвоение СНИМАЕТ — но только его: если
   * количество успели поправить руками (скажем, поставили три), программа
   * его не трогает, потому что это уже решение человека, а не её.
   *
   * Прочие доп. элементы не удваиваются: крючки или винт на две челюсти
   * ставят по потребности, а не парой.
   */
  const chooseJaw = (jaw: Jaw) => {
    const было = local.jaw;
    setLocal((current) => ({ ...current, jaw }));

    const обе = jaw === 'Обе челюсти';
    const былоОбе = было === 'Обе челюсти';
    if (обе === былоОбе) return;

    const печать = modelPrintExtraId(constructions);
    const сколько = обе ? 2 : 1;
    const естьПечать = печать ? local.extras.some((e) => e.id === печать) : false;
    /* Сама конструкция может БЫТЬ печатью модели — тогда доп. печать не нужна. */
    const конструкцияПечать = isModelPrintBase(
      constructions.find((k) => k.id === local.constructionId),
    );

    let extras = local.extras;
    if (печать && естьПечать) {
      // Удваиваем только «своё» количество: правленное руками не трогаем.
      extras = local.extras.map((e) =>
        e.id === печать && (обе ? e.qty === 1 : e.qty === 2) ? { ...e, qty: сколько } : e,
      );
    } else if (
      печать &&
      обе &&
      isNew &&
      local.constructionId &&
      !конструкцияПечать &&
      !печатьТронута.current
    ) {
      /*
       * Печати в заказе ещё нет, а челюстей две — значит, печатать нужно
       * две модели. Это тот же случай, что и подстановка печати при выборе
       * конструкции, только порядок заполнения обратный.
       */
      extras = [...local.extras, { id: печать, qty: 2 }];
    }

    // Количество конструкции трогаем, только если оно «наше»: 1 ↔ 2.
    const количество =
      можноМенятьКоличество(local.constructionQty, обе) ? сколько : local.constructionQty;

    applyCascade(
      applyConstructionQtyChange(
        applyExtrasChange(cascadeState(), extras, ctx),
        количество,
        ctx,
      ),
    );
  };

  const toggleExtra = (id: string, qty: number) => {
    // Печать модели, которую тронули руками, программа больше не расставляет.
    if (id === modelPrintExtraId(constructions)) печатьТронута.current = true;
    const next = qty <= 0
      ? local.extras.filter((e) => e.id !== id)
      : local.extras.some((e) => e.id === id)
        ? local.extras.map((e) => (e.id === id ? { ...e, qty } : e))
        : [...local.extras, { id, qty }];
    applyCascade(applyExtrasChange(cascadeState(), next, ctx));
  };

  /**
   * «Пусто» в списке элементов — снять всё выбранное разом.
   *
   * Снимать по одному нельзя было бы даже в цикле: каждый вызов считает
   * от одного и того же состояния карточки, и уцелел бы только последний.
   */
  const clearExtras = () => {
    // Снятую руками печать модели программа больше не подставляет сама.
    печатьТронута.current = true;
    applyCascade(applyExtrasChange(cascadeState(), [], ctx));
  };

  return (
    <CardWindow
      ключ="otc.orderWindow"
      title={isNew ? 'Новый заказ' : `Заказ · ${local.patient || 'без пациента'}`}
      subtitle={local.invoiceId ? `Включён в счёт/акт №${local.invoiceNumber ?? '?'}` : undefined}
      onClose={onClose}
      поУмолчанию={поУмолчанию}
    >
      {(ширина) => {
        /*
         * Два столбца — по ШИРИНЕ ОКНА, а не экрана: его тянут за край, и
         * запрос @media об этом ничего не знает.
         */
        const две = ширина >= ШИРИНА_ДВА;

        /*
         * Примечания стоят в РАЗНЫХ местах.
         *
         * В два столбца — последним полем справа: левый столбец с врачом,
         * клиникой, конструкцией и элементами и без них выходит вдвое выше,
         * а внизу правого оставалась пустая полоса в треть карточки.
         * В один столбец (телефон, узкое окно) они возвращаются на своё
         * прежнее место — сразу после цвета, как было до двух столбцов.
         */
        const примечания = (
          <Field label="Примечания">
            <textarea
              className="otc-input resize-none"
              rows={2}
              placeholder="Пожелания по форме, рисунку, надписи…"
              value={local.notes}
              onChange={(e) => setLocal({ ...local, notes: e.target.value })}
            />
          </Field>
        );
        return (
          <>
            {/*
              На ШИРОКОЙ карточке поля идут В ДВА СТОЛБЦА.

              Слева — что за работа: врач, клиника, пациент, конструкция,
              элементы, цвет. Справа — сроки, состояние и деньги. В один
              столбец карточка была вдвое выше экрана, и до кнопки
              «Применить» каждый раз прокручивали.

              На узкой карточке и на телефоне столбцы просто встают друг под
              друга в том же порядке — второй раз раскладку писать не нужно.
            */}
            <div
              className={две ? 'grid items-start gap-x-6' : 'space-y-4'}
              style={две ? { gridTemplateColumns: '1fr 1fr' } : undefined}
            >
              <div className="min-w-0 space-y-4">
                <Field
                  label="Врач"
                  hint={
                    local.doctorId === ''
                      ? 'Можно оставить пустым: из клиники привозят работу раньше, чем называют врача'
                      : undefined
                  }
                >
                  {/*
                    Пока врач не выбран, поле бледное — как «Выбрать конструкцию»
                    ниже. Прежде здесь стоял первый по алфавиту врач, и его легко
                    было принять за выбранного.

                    Подпись пустого пункта разная. В новом заказе это приглашение
                    выбрать; в уже заведённом — состояние «ещё не выяснили, кто ведёт
                    работу», и звать выбирать поздно: заказ уже живёт.
                  */}
                  <DoctorPicker
                    doctors={doctors}
                    selectedId={local.doctorId}
                    isNew={isNew}
                    onChange={(id) => applyCascade(applyDoctorChange(cascadeState(), id, ctx))}
                  />

                </Field>

                <Field
                  label="Клиника / Филиал"
                  hint={
                    local.doctorId === ''
                      ? 'Подставится сама, когда выберете врача'
                      : local.clinicId === ''
                      ? 'Частный заказ — счёт по нему выставить нельзя'
                      : doctor && doctor.clinics.length > 0
                        ? `Показаны клиники, где работает ${doctor.name}`
                        : 'У врача не указаны клиники — показаны все'
                  }
                >
                  {/*
                    Пока клиника не выбрана, поле бледное — как соседние. Цвет
                    пунктов задан явно: иначе весь раскрытый список наследовал бы
                    бледность поля и выглядел бы недоступным.
                  */}
                  <div className="mb-2">
                    <ClinicPicker
                      clinics={availableClinics}
                      selectedId={local.clinicId}
                      onChange={(id) => applyCascade(applyClinicChange(cascadeState(), id, ctx))}
                    />
                  </div>

                  {clinic && clinic.branches.length > 1 && (
                    <ChoicePicker
                      value={local.branchId}
                      options={clinic.branches.map((b) => ({ value: b.id, label: b.name }))}
                      onChange={(id) => applyCascade(applyBranchChange(cascadeState(), id, ctx))}
                    />
                  )}
                </Field>

                {/*
                  Пациент и челюсть — ОДНОЙ строкой.

                  Короткое поле и выбор из четырёх пунктов: в широком окне
                  каждому хватает половины ширины. Порознь они занимали два ряда —
                  из таких мелочей и набиралась высота, из-за которой карточку
                  приходилось прокручивать.

                  НА ТЕЛЕФОНЕ они снова друг под другом: на 390 точках в половине
                  ширины не помещается ни фамилия пациента, ни «Верхняя челюсть».
                */}
                <div className={две ? 'grid grid-cols-2 gap-3' : 'space-y-4'}>
                  <Field label="Пациент (ФИО)" hint="Можно заполнить позже">
                    <input
                      className="otc-input"
                      placeholder="Не указан"
                      value={local.patient}
                      onChange={(e) => setLocal({ ...local, patient: e.target.value })}
                    />
                  </Field>

                  {/*
                    Челюсть — ПЕРЕД конструкцией и отдельным полем.

                    Это приписка к названию работы, а не свойство изделия: одна и та же
                    конструкция делается и на верхнюю, и на нижнюю челюсть, цена от
                    этого не меняется. Держать в справочнике по две позиции на каждую
                    значило бы удвоить его, не добавив ни одного нового аппарата.
                  */}
                  <Field
                    label="Челюсть"
                    hint={
                      local.jaw === 'Обе челюсти'
                        ? 'Два аппарата: количество конструкции и печати модели — по два'
                        : 'Дописывается к названию работы в счёте, акте и письме врачу'
                    }
                  >
                    {/*
                      Пока челюсть не выбрана, подпись БЛЕДНАЯ — как подсказка в пустом
                      поле ввода. Тем же чёрным, что и выбранное значение, «Не указывать»
                      читалось как сделанный выбор, хотя это просто нетронутое поле.
                    */}
                    <ChoicePicker
                      value={local.jaw}
                      placeholder="Не указывать"
                      options={[
                        /*
                          «Не указывать» первым и по умолчанию: часть аппаратов
                          делается всегда на одну и ту же челюсть, уточнять там
                          нечего — тогда приписки в названии работы не появляется.
                        */
                        { value: '', label: 'Не указывать' },
                        { value: 'Верхняя челюсть', label: 'Верхняя челюсть' },
                        { value: 'Нижняя челюсть', label: 'Нижняя челюсть' },
                        /*
                          «Обе челюсти» — два аппарата по одному заказу: в названии
                          работы появляется «на верх.чел. и нижн.чел.», а количество
                          конструкции и печати модели программа ставит двойкой.
                        */
                        { value: 'Обе челюсти', label: 'Обе челюсти' },
                      ]}
                      onChange={(v) => chooseJaw(v as Jaw)}
                    />
                  </Field>
                </div>

                <Field
                  label="Конструкция"
                  hint={positionsHint(constructions.filter((k) => k.type === 'Базовая конструкция').length)}
                >
                  <ConstructionPicker
                    groups={baseGroups}
                    selectedId={local.constructionId}
                    pay={payДляЦен}
                    onChange={chooseConstruction}
                  />

                </Field>

                {/*
                  Количество конструкции — ТОЙ ЖЕ строкой, что у доп. элементов:
                  рамка, счётчик, сумма позиции справа. Два соседних поля делают
                  одно и то же, и разный вид заставлял разбираться в каждом заново.
    
                  Строка стоит ВНЕ поля «Конструкция»: подпись поля («40 позиций по
                  категориям») выводится под содержимым, и счётчик отрезал её от
                  списка — она читалась как пояснение к количеству.
                */}
                {/*
                  Счётчик показывается ТОЛЬКО у конструкций, которым это разрешено
                  в справочнике. У большинства аппаратов пара одинаковых
                  бессмысленна: их делают по слепку конкретного пациента.
                */}
                {construction && (
                  <div
                    className="-mt-2 divide-y rounded-lg border"
                    style={{ ...ВЫДЕЛЕНО, background: 'var(--otc-surface)' }}
                  >
                    <ChosenRow
                      name={construction.name}
                      hint={
                        local.constructionQty > 1
                          ? `по ${formatKop(priceOf(construction, payДляЦен))} ₽`
                          : undefined
                      }
                      /* Счётчик — только у позиций, которым это разрешено в справочнике: */
                      /* пара одинаковых аппаратов бессмысленна, их делают по слепку пациента. */
                      qty={construction.qtyAdjustable ? local.constructionQty : null}
                      onQty={(next) => applyCascade(applyConstructionQtyChange(cascadeState(), next, ctx))}
                      priceKop={priceOf(construction, payДляЦен) * local.constructionQty}
                      onRemove={() => chooseConstruction('')}
                      removeTitle="Убрать конструкцию"
                    />
                  </div>
                )}

                {construction?.hasSlm && (
                  <Field label="Производитель каркаса">
                    <select
                      aria-label="Производитель каркаса"
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)', color: 'var(--otc-ink)' }}
                      value={local.slmSupplier ?? 'M'}
                      onChange={(e) => {
                        const supplier = e.target.value as SlmSupplier;
                        setLocal({ ...local, slmSupplier: supplier, expenseKop: defaultExpenseKop(local.constructionId, ctx.catalog, local.constructionQty, local.extras, supplier) });
                      }}
                    >
                      {SLM_SUPPLIERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </Field>
                )}
                <Field label="Дополнительные элементы">
                  <ExtrasPicker
                    groups={extraGroups}
                    selected={local.extras}
                    pay={payДляЦен}
                    onChange={toggleExtra}
                    onClear={clearExtras}
                  />
                </Field>

                <Field label={construction?.hasColor ? 'Цвет (можно выбрать несколько)' : 'Цвет'}>
                  {construction?.hasColor ? (
                    <ColorPicker
                      colors={colors}
                      categories={categories}
                      selected={local.colors}
                      onChange={(next) => setLocal({ ...local, colors: next })}
                    />
                  ) : (
                    <div className="flex min-h-[38px] items-center rounded-lg border px-3 text-sm text-ink2"
                         style={{ borderColor: 'var(--otc-line)' }}>
                      —
                    </div>
                  )}
                </Field>

                {!две && примечания}
              </div>

              <div className="min-w-0 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Дата получения">
                    <DateField
                      value={local.received}
                      // Дату получения снять нельзя: заказ откуда-то пришёл.
                      onChange={(next) => setLocal({ ...local, received: next ?? local.received })}
                    />
                  </Field>
                  <Field
                    label="Дата сдачи"
                    hint={local.due ? undefined : 'Без срока заказ считается «В работе»'}
                  >
                    <DateField
                      value={local.due}
                      // Снятая дата — заказ без срока, он считается «В работе».
                      clearable
                      clearLabel="Без срока"
                      placeholder="Без срока"
                      onChange={(next) => setLocal({ ...local, due: next })}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Статус выполнения">
                    {/*
                      Список статусов ПОЛНЫЙ, включая «Ожидание».

                      Раньше его здесь не было: статус появился позже, и правили его
                      только быстрым выбором в журнале. Замороженный доктором заказ
                      при этом открывался в карточке с пустым полем статуса — а
                      сохранение карточки записало бы обратно пустоту.
                    */}
                    <ChoicePicker
                      value={local.status}
                      options={[
                        { value: 'В очереди', label: 'В очереди' },
                        { value: 'В работе', label: 'В работе' },
                        { value: 'Ожидание', label: 'Ожидание' },
                        { value: 'Готово', label: 'Готово' },
                      ]}
                      onChange={(v) => setLocal({ ...local, status: v as OrderStatus })}
                    />
                  </Field>
                  <Field label="Срочность">
                    <ChoicePicker
                      value={local.urgency}
                      /*
                        У «Готово» и «Ожидание» срочность не действует: работа либо
                        закончена, либо остановлена доктором, и «очень срочно» на
                        ней означало бы неправду. Правило то же, что в журнале.
                      */
                      disabled={local.status === 'Готово' || local.status === 'Ожидание'}
                      options={[
                        { value: 'Авто', label: 'Авто' },
                        { value: 'Очень срочно', label: 'Очень срочно' },
                        { value: 'Срочно', label: 'Срочно' },
                        { value: 'В работе', label: 'В работе' },
                      ]}
                      onChange={(v) => setLocal({ ...local, urgency: v as Urgency })}
                    />
                  </Field>
                </div>

                <div className="-mt-2 flex items-center gap-2">
                  <Badge color={STATUS_COLORS[urgency.tone]}>{urgency.label}</Badge>
                </div>

                {/*
                  Оплата, стоимость и расход — ОДНОЙ СЕТКОЙ на два столбца, но
                  расставлены по-разному.

                  В широком окне «Способ оплаты» занимает всю ширину (полная
                  подпись «Выбрать способ оплаты» в половину не влезает), а под
                  ним парой идут стоимость и расход — два поля с рублями,
                  которые и заполняют подряд.

                  НА ТЕЛЕФОНЕ порядок прежний: оплата и стоимость в одной
                  строке, расход под ними во всю ширину — так было до того, как
                  карточку разложили в два столбца.
                */}
                <div className="grid grid-cols-2 gap-3">
                  <div className={две ? 'col-span-2' : undefined}>
                    <Field label="Способ оплаты">
                      <ChoicePicker
                        value={local.pay}
                        /*
                          Подсказка живёт только в закрытом поле: вернуть заказ
                          в «не определён» нельзя — сохранённый заказ без способа
                          оплаты не бывает, и такого пункта в списке нет.

                          Поле идёт во всю ширину столбца — полная подпись
                          помещается и на телефоне.
                        */
                        placeholder="Выбрать способ оплаты"
                        options={[
                          { value: 'По договору', label: 'По договору' },
                          { value: 'Наличные', label: 'Наличные' },
                        ]}
                        onChange={(v) => choosePay(v as PayMethod)}
                      />
                      {/* Ручное переопределение показывается явно, а не молча (§1). */}
                      {override.overridden && (
                        <p className="mt-1 text-[11px]" style={{ color: 'var(--otc-amber)' }}>
                          Изменено вручную — у клиники по умолчанию «{override.defaultPay}»
                        </p>
                      )}
                    </Field>
                  </div>
                  <Field label="Стоимость, ₽">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="otc-input tabular"
                      value={formatKop(local.costKop)}
                      onChange={(e) => setLocal({ ...local, costKop: rublesToKop(e.target.value) })}
                    />
                  </Field>
                  <div className={две ? undefined : 'col-span-2'}>
                    <Field label="Расход, ₽">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="otc-input tabular"
                        value={formatKop(local.expenseKop)}
                        onChange={(e) => setLocal({ ...local, expenseKop: rublesToKop(e.target.value) })}
                      />
                    </Field>
                  </div>
                </div>

                <div
                  className="flex items-center justify-between rounded-lg px-3 py-2"
                  style={{ background: 'var(--otc-surface)', border: '1px solid var(--otc-line)' }}
                >
                  <span className="text-sm text-ink2">Доход</span>
                  <span
                    className="tabular text-sm font-medium"
                    style={{ color: profitKop >= 0 ? STATUS_COLORS.done : STATUS_COLORS.danger }}
                  >
                    {formatKop(profitKop)} ₽
                  </span>
                </div>

                {/*
                  Оплата — МЕТКА И КНОПКА РЯДОМ, как в карточке счёта.

                  Раньше здесь стоял один прямоугольный переключатель: он был и
                  состоянием, и действием сразу, и по нему не читалось, что
                  произойдёт по нажатию. Теперь слева цветная метка — «Оплачен»
                  зелёным, «Не оплачен» красным, потому что это долг, — а справа
                  белая кнопка с прямым названием действия.
                */}
                <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
                  <Pilule
                    look={
                      local.paid
                        ? { color: STATUS_COLORS.done, icon: CheckCircle2, label: 'Оплачен' }
                        : { color: STATUS_COLORS.danger, icon: Wallet, label: 'Не оплачен' }
                    }
                  />
                  <ActionPill
                    icon={local.paid ? Wallet : CheckCircle2}
                    onClick={() => setLocal({ ...local, paid: !local.paid })}
                    title={
                      local.paid ? 'Снять отметку оплаты с этого заказа' : 'Отметить заказ оплаченным'
                    }
                  >
                    {local.paid ? 'Снять оплату' : 'Отметить оплаченным'}
                  </ActionPill>
                  {/*
                    Флажок доступен и когда заказ в счёте. Счёт становится оплаченным
                    по своим заказам, а не наоборот, и отмечают их здесь.
                  */}
                  {local.invoiceNumber && (
                    <span className="text-ink2">счёт № {local.invoiceNumber}</span>
                  )}
                </div>

                {две && примечания}

                {/*
                  Здесь стояла плашка «Вложения появятся вместе с загрузкой файлов».
                  Убрана: загрузки нет — ни маршрута на сервере, ни поля выбора
                  файла, — и плашка обещала то, чего сделать нельзя. Место под
                  вложения в базе есть (`order_files`), так что появятся они здесь
                  же, когда будут написаны по-настоящему.
                */}
              </div>
            </div>


            {/*
              Заказ сохраняется ПУСТЫМ — намеренно.

              Его заводят по звонку: известен один пациент, а иногда и только
              клиника. Врач, изделие, срок и способ оплаты выясняются по ходу
              работы, и упираться в обязательное поле в этот момент незачем —
              дозаполнить карточку можно в любой день.
            */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <Button onClick={() => onSave(local)}>{isNew ? 'Создать заказ' : 'Применить'}</Button>
              <Button variant="secondary" onClick={onClose}>Отмена</Button>
            </div>

            {/*
              ДОКУМЕНТЫ ПО ЗАКАЗУ — прямо отсюда.

              Раньше за счётом и отчётом приходилось закрывать карточку,
              находить тот же заказ в журнале, отмечать его галочкой и только
              потом нажимать кнопку в нижней полосе — четыре действия ради
              одного документа по заказу, который уже открыт.

              Обе кнопки сперва ПРИМЕНЯЮТ правки: счёт выставляется по тому,
              что лежит на сервере, и уйти он должен с той ценой, которую
              человек видит перед собой, а не с прежней.

              Счёт уже выставлен — вместо создания открывается он сам: второй
              счёт по одному заказу это не создание, а ошибка.
            */}
            {!isNew && (onInvoice || onOpenInvoice || onReport) && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {local.invoiceId && onOpenInvoice ? (
                  <ДействиеКарточки
                    icon={FileSpreadsheet}
                    label={`Открыть счёт/акт №${local.invoiceNumber ?? '?'}`}
                    onClick={() => {
                      onSave(local, true);
                      onOpenInvoice();
                    }}
                  />
                ) : (
                  <ДействиеКарточки
                    icon={FileSpreadsheet}
                    label="Создать счёт/акт"
                    disabled={!onInvoice || Boolean(invoiceHint)}
                    title={invoiceHint ?? undefined}
                    onClick={() => {
                      onSave(local, true);
                      onInvoice?.();
                    }}
                  />
                )}
                {onReport && (
                  <ДействиеКарточки
                    icon={FileText}
                    label="Создать отчёт"
                    onClick={() => {
                      onSave(local, true);
                      onReport();
                    }}
                  />
                )}
                {invoiceHint && !local.invoiceId && (
                  <span className="text-[11px] text-ink2">{invoiceHint}</span>
                )}
              </div>
            )}

            {/*
              Отправка письма врачу — отдельной кнопкой, а не только окном при
              сохранении. Окно показывается один раз, и «Не отправлять» раньше
              закрывало путь совсем: передумать было нельзя.

              Подпись зависит от статуса: пока заказ в работе, уместно письмо о
              приёме; как перешёл в «Готово» — о готовности. Тем же правилом
              окрашена отметка в списке заказов.
            */}
            {/*
              Уведомление врачу и ярлыки мессенджеров — ОДНОЙ строкой в самом
              низу карточки.

              Это два способа сказать врачу одно и то же, и место им рядом, в
              конце: раньше ярлыки стояли под фамилией, посреди полей, где их
              принимали за часть выбора врача. Ярлыки прижаты к правому краю,
              чтобы не спорили взглядом с кнопкой письма.
            */}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {letterState && (
                <div className="min-w-0">
                  {/*
                    Подпись идёт за статусом НА ЭКРАНЕ, а отправка — только за
                    сохранённым. Иначе одно из двух: либо кнопка называет письмо,
                    которого не ждёшь, либо врач получает «заказ готов» о заказе,
                    который в базе ещё в работе.
                  */}
                  {letterState.blocked ? (
                    <p className="inline-flex items-center gap-1.5 text-xs text-ink2">
                      <Mail size={14} className="opacity-60" />
                      {letterState.blocked}
                    </p>
                  ) : letterState.sentKinds.includes(letterDue(local.status)) ? (
                    <p className="inline-flex items-center gap-1.5 text-xs" style={{ color: STATUS_COLORS.done }}>
                      <MailCheck size={14} />
                      Письмо {letterLabel(local.status)} уже отправлено
                    </p>
                  ) : local.status !== letterState.savedStatus ? (
                    <p className="inline-flex items-center gap-1.5 text-xs" style={{ color: 'var(--otc-amber)' }}>
                      <Mail size={14} />
                      Сначала примените изменение статуса — тогда можно будет отправить письмо{' '}
                      {letterLabel(local.status)}
                    </p>
                  ) : (
                    /*
                      Кнопка письма ВЫДЕЛЕНА рамкой цвета темы на белом.

                      Серая на сером она сливалась с фоном карточки и читалась
                      подписью, а не действием — письмо врачу забывали отправить
                      просто потому, что не замечали, чем его отправить. Заливкой
                      делать нельзя: это не главное действие карточки, главное —
                      «Применить», и две сплошные кнопки рядом спорили бы.
                    */
                    <button
                      onClick={() => onSendLetter(letterDue(local.status))}
                      className="inline-flex items-center gap-1.5 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors"
                      style={{
                        borderColor: 'var(--otc-accent)',
                        color: 'var(--otc-accent)',
                        background: 'var(--otc-surface)',
                      }}
                    >
                      <Mail size={14} />
                      Отправить уведомление {letterLabel(local.status)}
                    </button>
                  )}
                </div>
              )}
              <span className="ml-auto">
                <Messengers contact={doctor} size={28} />
              </span>
            </div>

            {!isNew && onDelete && (
              <div className="mt-3">
                {confirmDelete ? (
                  <ConfirmInline
                    /*
                      Про счёт сказано ДО нажатия, а не после отказа. Удаление
                      уносит счёт с собой, и узнать об этом постфактум — значит
                      узнать слишком поздно: вернуть его будет нечем.
                    */
                    question={
                      local.invoiceNumber
                        ? `Удалить заказ безвозвратно? Счёт № ${local.invoiceNumber} тоже будет удалён.`
                        : 'Удалить заказ безвозвратно?'
                    }
                    onConfirm={onDelete}
                    onCancel={() => setConfirmDelete(false)}
                  />
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs hover:bg-black/5"
                    style={{ color: 'var(--otc-coral)' }}
                  >
                    <Trash2 size={13} /> Удалить заказ
                  </button>
                )}
              </div>
            )}
          </>
        );
      }}
    </CardWindow>
  );
}

/**
 * Кнопка действия карточки: рамка, значок, подпись.
 *
 * Не «Button»: главная кнопка карточки одна — «Применить», и вторая сплошная
 * рядом спорила бы с ней. Обведённая читается как действие, но не как итог.
 */
function ДействиеКарточки({
  icon: Icon,
  label,
  onClick,
  disabled,
  title,
}: {
  icon: (props: { size?: number }) => ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm text-ink transition-colors hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-45"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------

/**
 * Выбор доп. элементов с количеством.
 *
 * Цена показывается для ТЕКУЩЕГО способа оплаты заказа: по договору и за
 * наличные она разная, и техник должен видеть ту, которая пойдёт в счёт.
 */
function ExtrasPicker({
  groups,
  selected,
  pay,
  onChange,
  onClear,
}: {
  groups: { name: string; items: ApiConstruction[] }[];
  selected: OrderExtra[];
  pay: PayMethod;
  onChange: (id: string, qty: number) => void;
  /** «Пусто»: снять все выбранные элементы разом. */
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const chosen = new Map(selected.map((e) => [e.id, e.qty]));
  const extras = groups.flatMap((group) => group.items);

  const search = query.trim().toLowerCase();
  const visibleGroups = search
    ? groups
        .map((group) => ({ ...group, items: group.items.filter((k) => k.name.toLowerCase().includes(search)) }))
        .filter((group) => group.items.length > 0)
    : groups;

  const totalKop = selected.reduce((sum, extra) => {
    const item = extras.find((k) => k.id === extra.id);
    return sum + (item ? priceOf(item, pay) * extra.qty : 0);
  }, 0);

  return (
    <div>
      <PickerField
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClose={() => setOpen(false)}
        placeholder="Выбрать элемент"
        /*
          В свёрнутом виде — НАЗВАНИЯ, как у выбора конструкции рядом.
          Было «Выбрано 1 · 1 000 ₽»: чтобы вспомнить, что именно выбрано,
          список приходилось раскрывать. Поле обязано отвечать на вопрос
          «что выбрано», а не «сколько штук».
        */
        /*
          В свёрнутом поле — ТОЛЬКО приглашение добавить и сумма.

          Раньше здесь перечислялись выбранные элементы, и те же элементы
          строками стояли под полем: один и тот же список читался дважды,
          а на узком экране верхняя строка обрывалась на полуслове.
        */
        summary={
          selected.length > 0 ? (
            <span className="flex w-full items-center justify-between gap-3">
              <span className="text-ink2">Добавить элемент</span>
              <span className="tabular flex-shrink-0 text-ink2">{formatKop(totalKop)} ₽</span>
            </span>
          ) : (
            ''
          )
        }
      >
        <input
          className="w-full border-b px-3 py-2 text-sm outline-none"
          style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
          placeholder={подсказкаПоиска('Поиск элемента')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="max-h-72 overflow-y-auto">
          {/*
            «Пусто» ПЕРВЫМ пунктом — как в выборе конструкции рядом.
            Смысл тот же: снять выбранное одним касанием. Убирать элементы
            по одному корзиной приходилось, даже когда весь набор оказался
            не тот — например, при смене конструкции.
          */}
          <button
            onClick={() => {
              onClear();
              setOpen(false);
            }}
            className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
            style={
              selected.length === 0
                ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
                : undefined
            }
          >
            <span className="text-ink2">Пусто</span>
          </button>
          {visibleGroups.map((group) => (
            <div key={group.name}>
              <p
                className="sticky top-0 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-ink2"
                style={{ background: 'var(--otc-bg)' }}
              >
                {group.name}
              </p>
              {group.items.map((item) => {
                const qty = chosen.get(item.id) ?? 0;
                return (
                  <button
                    key={item.id}
                    onClick={() => onChange(item.id, qty + 1)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-black/5"
                  >
                    <span className="min-w-0 text-ink">
                      {item.name}
                      {/* Уже добавленные видны сразу — иначе легко положить дважды. */}
                      {qty > 0 && <span className="ml-1.5 text-ink2">· выбрано {qty}</span>}
                    </span>
                    <span className="tabular flex-shrink-0 text-ink2">
                      {formatKop(priceOf(item, pay))} ₽
                    </span>
                  </button>
                );
              })}
            </div>
          ))}

          {visibleGroups.length === 0 && (
            <p className="px-3 py-2 text-sm text-ink2">Ничего не найдено</p>
          )}
        </div>
      </PickerField>

      {/*
        Выбранное — СТРОКАМИ, а не метками в один ряд. В метке количество
        было мелким «×2» в хвосте, кнопки «+» и «−» — символами в несколько
        пикселей, а цена не показывалась вовсе. Строка вмещает название,
        полноразмерный счётчик и сумму позиции.
      */}
      {selected.length > 0 && (
        <div
          className="mt-2 divide-y rounded-lg border"
          style={{ ...ВЫДЕЛЕНО, background: 'var(--otc-surface)' }}
        >
          {selected.map((extra) => {
            const item = extras.find((k) => k.id === extra.id);
            if (!item) return null;
            return (
              /*
                На телефоне название занимает свою строку, а счётчик, сумма и
                корзина встают под ним.

                В один ряд на 390 точках названию оставалось около сотни, и
                «Печать 3D модели» обтекало счётчик тремя строками: «Печать»,
                «3D», «модели» — по бокам от кнопок «−» и «+». Читалось как
                поломка вёрстки, чем и было.
              */
              <ChosenRow
                key={extra.id}
                name={item.name}
                hint={extra.qty > 1 ? `по ${formatKop(priceOf(item, pay))} ₽` : undefined}
                qty={extra.qty}
                onQty={(next) => onChange(extra.id, next)}
                priceKop={priceOf(item, pay) * extra.qty}
                onRemove={() => onChange(extra.id, 0)}
                removeTitle="Убрать элемент"
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Подпись о размере справочника: «1 позиция», «2 позиции», «40 позиций».
 *
 * Число подставлялось в неизменяемое «позиций», и при двух базовых
 * конструкциях подпись читалась как машинный вывод, а не как русский текст.
 */
function positionsHint(count: number): string {
  const tail = count % 100;
  const last = count % 10;
  const word =
    tail >= 11 && tail <= 14 ? 'позиций' : last === 1 ? 'позиция' : last >= 2 && last <= 4 ? 'позиции' : 'позиций';
  return `${count} ${word} по категориям`;
}

/**
 * Ширина боковой карточки — свойство РАБОЧЕГО МЕСТА, а не лаборатории.
 *
 * На широком мониторе удобно раскрыть карточку пошире и видеть журнал
 * рядом, на ноутбуке — наоборот. Поэтому ширина живёт в браузере, как
 * ширины колонок журнала, и не навязывается другим сотрудникам.
 */
/**
 * Где окно заказа появляется в первый раз — У ПРАВОГО КРАЯ, ДО СТОЛБЦА
 * «СТАТУС».
 *
 * Границу назвал владелец: слева должно оставаться видно всё, по чему заказ
 * узнают, — номер, врач, клиника, конструкция, пациент и обе даты; статус,
 * деньги и счёт окно закрывает собой, но их же и показывает. Дальше окно
 * таскают и тянут как удобно, и место запоминается.
 *
 * Считается ОДИН РАЗ, при первом открытии: дальше своё место берётся из
 * хранилища (см. `CardWindow`).
 */
function поУмолчанию(): Окно {
  const шапка = [...document.querySelectorAll('thead th')].find((th) =>
    (th.textContent ?? '').trim().startsWith('Статус'),
  );
  const лево = шапка ? шапка.getBoundingClientRect().left : window.innerWidth - ШИРИНА_ДВА;
  const w = Math.max(ШИРИНА_ДВА, window.innerWidth - лево - ОТСТУП);
  return зажать({ x: window.innerWidth - w - ОТСТУП, y: ОТСТУП, w });
}
/**
 * Строка выбранного — ОДНА на конструкцию и на доп. элементы.
 *
 * Раньше эти строки были написаны порознь и разошлись: у количества
 * конструкции подпись «Количество» вместо названия работы, у элементов —
 * корзина, у конструкции — пустое место вместо неё. Теперь вид задан кодом,
 * а не повторён руками, и разойтись им негде.
 *
 * На ТЕЛЕФОНЕ название занимает свою строку, а счётчик, сумма и корзина
 * встают под ним: в один ряд на 390 точках названию оставалось около сотни
 * точек, и «Печать 3D модели» обтекало счётчик тремя строками.
 */
function ChosenRow({
  name,
  hint,
  qty,
  onQty,
  priceKop,
  onRemove,
  removeTitle,
}: {
  name: string;
  /** Приписка к названию: цена за штуку, когда их несколько. */
  hint?: string;
  /** Количество; `null` — у этой позиции его менять нельзя. */
  qty: number | null;
  onQty?: (next: number) => void;
  priceKop: number;
  onRemove: () => void;
  removeTitle: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
      <span className="min-w-0 flex-1 basis-full text-sm text-ink sm:basis-0">
        {name}
        {hint && <span className="ml-1.5 text-xs text-ink2">{hint}</span>}
      </span>
      {qty !== null && onQty ? (
        <QtyStepper qty={qty} onChange={onQty} />
      ) : (
        /* Место счётчика: суммы обеих строк стоят в одном столбце. */
        <span className="hidden h-[34px] w-[104px] flex-shrink-0 sm:block" aria-hidden />
      )}
      <span className="tabular ml-auto w-20 flex-shrink-0 text-right text-sm text-ink sm:ml-0">
        {formatKop(priceKop)} ₽
      </span>
      <button onClick={onRemove} className="flex-shrink-0 rounded p-1 hover:bg-black/5" title={removeTitle}>
        <Trash2 size={13} className="text-ink2" />
      </button>
    </div>
  );
}

/**
 * Выбор конструкции.
 *
 * Тем же раскрывающимся полем, что доп. элементы и цвет. Раньше здесь стоял
 * системный `<select>`: он выглядел иначе, открывался иначе и не умел
 * искать — а базовых конструкций около сорока, и листать их до нужной
 * приходилось глазами.
 *
 * Выбор ОДИНОЧНЫЙ, поэтому список закрывается сразу после щелчка: добавлять
 * в заказ вторую базовую конструкцию нельзя, ждать больше нечего.
 */
function ConstructionPicker({
  groups,
  selectedId,
  pay,
  onChange,
}: {
  groups: { name: string; items: ApiConstruction[] }[];
  selectedId: string;
  pay: PayMethod;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const all = groups.flatMap((group) => group.items);
  const selected = all.find((item) => item.id === selectedId);

  const search = query.trim().toLowerCase();
  const visibleGroups = search
    ? groups
        .map((group) => ({ ...group, items: group.items.filter((k) => k.name.toLowerCase().includes(search)) }))
        .filter((group) => group.items.length > 0)
    : groups;

  const choose = (id: string) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <PickerField
      open={open}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      placeholder="Выбрать конструкцию"
      /*
        В закрытом поле — ПРИГЛАШЕНИЕ и сумма, а что выбрано, показывает
        строка под полем. Точно так же устроены доп. элементы: два соседних
        поля делают одно и то же, и разный вид заставлял разбираться в каждом
        заново — выбранная конструкция читалась внутри поля, выбранные
        элементы — строками под ним.
      */
      summary={
        selected ? (
          <span className="flex w-full items-center justify-between gap-3">
            <span className="text-ink2">Сменить конструкцию</span>
            <span className="tabular flex-shrink-0 text-ink2">{formatKop(priceOf(selected, pay))} ₽</span>
          </span>
        ) : (
          ''
        )
      }
    >
      <input
        className="w-full border-b px-3 py-2 text-sm outline-none"
        style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
        placeholder={подсказкаПоиска('Поиск конструкции')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="max-h-72 overflow-y-auto">
        {/*
          «Пусто» ПЕРВЫМ пунктом: заказ заводят по звонку врача, когда изделие
          ещё не названо — известны пациент и срок. Стоимость такого заказа
          нулевая, и это «пока не посчитано», а не ошибка.
        */}
        <button
          onClick={() => choose('')}
          className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
          style={selectedId === '' ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' } : undefined}
        >
          <span className="text-ink2">Пусто</span>
        </button>
        {visibleGroups.map((group) => (
          <div key={group.name}>
            <p
              className="sticky top-0 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-ink2"
              style={{ background: 'var(--otc-bg)' }}
            >
              {group.name}
            </p>
            {group.items.map((item) => {
              const active = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  onClick={() => choose(item.id)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-black/5"
                  style={active ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' } : undefined}
                >
                  <span className="min-w-0 text-ink">{item.name}</span>
                  <span className="tabular flex-shrink-0 text-ink2">
                    {formatKop(priceOf(item, pay))} ₽
                  </span>
                </button>
              );
            })}
          </div>
        ))}

        {visibleGroups.length === 0 && (
          <p className="px-3 py-2 text-sm text-ink2">Ничего не найдено</p>
        )}
      </div>
    </PickerField>
  );
}

/**
 * Раскрывающееся поле — общий вид для доп. элементов и цветов.
 *
 * Один вид на все три подряд идущих выбора формы — конструкцию, доп. элементы
 * и цвет. Раньше каждый открывался по-своему: конструкция системным списком
 * браузера, элементы из-под пунктирной кнопки «Добавить элемент», а все два
 * десятка цветов лежали развёрнутыми и занимали пол-экрана.
 *
 * Список раскрывается ВНУТРИ панели, а не всплывающим окном: панель сама
 * прокручивается, и всплывающий список пришлось бы позиционировать заново
 * при каждой прокрутке.
 */
/**
 * Выбор клиники — тем же списком с поиском, что и врач.
 *
 * Клиник в базе несколько десятков, а названия у них длинные и похожие
 * («Клиника экспертной ортодонтии…»): на телефоне системный список
 * приходилось листать, вчитываясь в каждую строку. Три буквы отбирают
 * нужную быстрее.
 *
 * Тип здесь минимальный — идентификатор и название: полю нужно ровно это,
 * а знать про филиалы, юрлица и договоры ему незачем.
 */
function ClinicPicker({
  clinics,
  selectedId,
  onChange,
}: {
  clinics: { id: string; name: string }[];
  selectedId: string;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const поиск = query.trim().toLowerCase();
  const видимые = поиск ? clinics.filter((c) => c.name.toLowerCase().includes(поиск)) : clinics;
  const выбрана = clinics.find((c) => c.id === selectedId);

  const choose = (id: string) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  return (
    <PickerField
      open={open}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      placeholder="Пусто"
      summary={выбрана ? <span className="min-w-0 truncate text-ink">{выбрана.name}</span> : ''}
    >
      <input
        className="w-full border-b px-3 py-2 text-sm outline-none"
        style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
        placeholder={подсказкаПоиска('Поиск клиники')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="max-h-72 overflow-y-auto">
        {/*
          «Пусто» ПЕРВЫМ пунктом: заказ бывает частным — пациент пришёл сам,
          клиники за ним нет. Раньше под такой случай заводили
          клинику-заглушку, и справочник засорялся.
        */}
        <button
          onClick={() => choose('')}
          className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
          style={
            selectedId === ''
              ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
              : undefined
          }
        >
          <span className="text-ink2">Пусто — частный заказ</span>
        </button>

        {видимые.map((c) => (
          <button
            key={c.id}
            onClick={() => choose(c.id)}
            className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
            style={
              c.id === selectedId
                ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
                : undefined
            }
          >
            <span className="min-w-0 text-ink">{c.name}</span>
          </button>
        ))}

        {видимые.length === 0 && (
          <p className="px-3 py-3 text-sm text-ink2">Ничего не нашли — проверьте название.</p>
        )}
      </div>
    </PickerField>
  );
}

/**
 * Выбор врача — списком С ПОИСКОМ, а не выпадающим списком браузера.
 *
 * Врачей в базе шесть десятков, и на телефоне обычный `select` открывается
 * системным списком во весь экран: до «Тарнуевой» приходится листать
 * пальцем полсотни фамилий, а искать в нём нечем. Набрать три буквы
 * быстрее любого листания — так уже устроен выбор конструкции рядом.
 *
 * Архивных врачей в списке нет — кроме того, кто уже стоит в этом заказе:
 * он там не для выбора, а чтобы заказ не выглядел безымянным.
 */
function DoctorPicker({
  doctors,
  selectedId,
  isNew,
  onChange,
}: {
  doctors: ApiDoctor[];
  selectedId: string;
  /** Новый заказ зовёт выбрать; у заведённого пустота — «ещё не выяснили». */
  isNew: boolean;
  onChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const доступные = doctors.filter((d) => !d.archived || d.id === selectedId);
  const поиск = query.trim().toLowerCase();
  const видимые = поиск
    ? доступные.filter((d) => d.name.toLowerCase().includes(поиск))
    : доступные;
  const выбран = doctors.find((d) => d.id === selectedId);

  const choose = (id: string) => {
    onChange(id);
    setQuery('');
    setOpen(false);
  };

  return (
    <PickerField
      open={open}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      выделено={Boolean(выбран)}
      placeholder={isNew ? 'Выбрать врача' : 'Врач не указан'}
      summary={
        выбран ? (
          <span className="min-w-0 truncate text-ink">
            {выбран.archived ? `${выбран.name} (в архиве)` : выбран.name}
          </span>
        ) : (
          ''
        )
      }
    >
      <input
        className="w-full border-b px-3 py-2 text-sm outline-none"
        style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
        placeholder={подсказкаПоиска('Поиск врача')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="max-h-72 overflow-y-auto">
        {/*
          «Пусто» ПЕРВЫМ пунктом, как у конструкции: работу привозят из
          клиники раньше, чем называют врача, и заказ заводят без него.
        */}
        <button
          onClick={() => choose('')}
          className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
          style={
            selectedId === ''
              ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
              : undefined
          }
        >
          <span className="text-ink2">{isNew ? 'Без врача' : 'Врач не указан'}</span>
        </button>

        {видимые.map((d) => (
          <button
            key={d.id}
            onClick={() => choose(d.id)}
            className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-black/5"
            style={
              d.id === selectedId
                ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
                : undefined
            }
          >
            <span className="min-w-0 text-ink">{d.name}</span>
            {d.archived && <span className="flex-shrink-0 text-xs text-ink2">в архиве</span>}
          </button>
        ))}

        {видимые.length === 0 && (
          <p className="px-3 py-3 text-sm text-ink2">Никого не нашли — проверьте фамилию.</p>
        )}
      </div>
    </PickerField>
  );
}

/**
 * Можно ли программе менять количество конструкции при смене челюсти.
 *
 * Только если оно осталось таким, каким его ставила она сама: один аппарат
 * при одной челюсти, два — при обеих. Поправленное руками количество —
 * решение человека, и переписывать его нельзя.
 */
function можноМенятьКоличество(qty: number, обе: boolean): boolean {
  return обе ? qty === 1 : qty === 2;
}

/**
 * Выбор из НЕСКОЛЬКИХ известных значений — тем же полем, что конструкция,
 * врач и клиника рядом, но без поиска.
 *
 * Системный список браузера выглядел в карточке чужим: на телефоне он
 * раскрывается во весь экран своим шрифтом и своими кружками, и поля
 * одного и того же заказа читались как из разных программ. Искать среди
 * четырёх пунктов нечего — поэтому строки поиска здесь нет, только
 * одинаковый вид.
 */
function ChoicePicker({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  /** Что показывать, пока значение пустое. */
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const выбран = options.find((o) => o.value === value);

  const поле = (
    <PickerField
      open={open}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      placeholder={placeholder ?? ''}
      summary={
        выбран && выбран.value !== '' ? (
          <span className="min-w-0 truncate text-ink">{выбран.label}</span>
        ) : (
          ''
        )
      }
    >
      <div className="max-h-72 overflow-y-auto">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => {
              onChange(o.value);
              setOpen(false);
            }}
            className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-black/5"
            style={
              o.value === value
                ? { background: 'color-mix(in srgb, var(--otc-accent) 12%, transparent)' }
                : undefined
            }
          >
            <span className={o.value === '' ? 'text-ink2' : 'text-ink'}>{o.label}</span>
          </button>
        ))}
      </div>
    </PickerField>
  );

  /*
   * Запертое поле (срочность у готового заказа) остаётся на месте и
   * читается, но не открывается: исчезни оно — раскладка бы прыгала, а
   * человек гадал, куда делась срочность.
   */
  return disabled ? <div className="pointer-events-none opacity-60">{поле}</div> : поле;
}

/**
 * ЗАМЕТНАЯ ОКАНТОВКА у главных полей карточки.
 *
 * Владелец назвал три места, куда смотрят в первую очередь: кто врач, что
 * за конструкция и какие доп. элементы уже выбраны. Остальные поля вокруг
 * обведены одинаковой бледной линией, и эти три в общем ряду терялись.
 * Цвет берётся из темы, поэтому окантовка меняется вместе с оформлением.
 */
const ВЫДЕЛЕНО = {
  borderColor: 'var(--otc-accent)',
  boxShadow: '0 0 0 1px color-mix(in srgb, var(--otc-accent) 30%, transparent)',
};

function PickerField({
  summary,
  placeholder,
  open,
  onToggle,
  onClose,
  выделено = false,
  children,
}: {
  /** Что выбрано — показывается в закрытом виде. */
  summary: ReactNode;
  placeholder: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** Заметная окантовка: поле из числа главных и уже заполнено. */
  выделено?: boolean;
  children: ReactNode;
}) {
  // Щелчок мимо сворачивает список — как у обычного выбора конструкции рядом.
  const ref = useOutsideClose<HTMLDivElement>(open, onClose);
  /*
   * Пустой МАССИВ в JavaScript истинный, поэтому проверка `summary ||` его
   * не ловила: поле цвета оставалось пустым вместо подсказки «Выбрать цвет».
   * Отсюда и явная проверка на длину.
   */
  const hasSummary = Array.isArray(summary)
    ? summary.filter(Boolean).length > 0
    : Boolean(summary);

  return (
    <div ref={ref}>
      <button
        onClick={onToggle}
        className="otc-input flex items-center justify-between gap-2 text-left"
        style={{ minHeight: 38, ...(выделено ? ВЫДЕЛЕНО : null) }}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {hasSummary ? summary : <span style={{ color: 'var(--otc-placeholder)' }}>{placeholder}</span>}
        </span>
        <ChevronDown
          size={15}
          className="flex-shrink-0 text-ink2 transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {open && (
        <div
          className="mt-1.5 overflow-hidden rounded-lg border"
          style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Счётчик количества доп. элемента.
 *
 * Полноразмерные кнопки, а не значки «+» и «−» внутри метки: попасть по
 * восьмипиксельному символу мышью трудно, а количество при этом читалось
 * мелким «×2» в конце строки. Здесь число стоит между кнопками и видно сразу.
 */
function QtyStepper({ qty, onChange }: { qty: number; onChange: (next: number) => void }) {
  const button = (label: string, next: number, title: string) => (
    <button
      onClick={() => onChange(next)}
      title={title}
      className="px-3 py-1.5 text-base leading-none text-ink2 transition-colors hover:bg-black/5 hover:text-ink"
    >
      {label}
    </button>
  );

  return (
    <div
      className="flex flex-shrink-0 items-center rounded-lg border"
      style={{ borderColor: 'var(--otc-line)', background: 'var(--otc-surface)' }}
    >
      {button('−', qty - 1, qty > 1 ? 'Меньше' : 'Убрать элемент')}
      <span className="tabular w-6 text-center text-sm font-medium text-ink">{qty}</span>
      {button('+', qty + 1, 'Больше')}
    </div>
  );
}

/**
 * Выбор цветов, сгруппированный по категориям — как в справочнике.
 *
 * Плоский список из двадцати с лишним оттенков заставлял искать нужный
 * глазами по всей строке. Категории («Standard», «Мультиколор», «Neon»,
 * «Disco») — те же, по которым цвета разложены в прайсе и в голове у техника.
 *
 * Служебный «Цвет не указан» вынесен наверх отдельно: он не относится ни
 * к одной категории и нужен чаще остальных.
 */
function ColorPicker({
  colors,
  categories,
  selected,
  onChange,
}: {
  colors: ApiColor[];
  categories: ApiCategory[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((c) => c !== id) : [...selected, id]);

  const chip = (color: ApiColor) => {
    const active = selected.includes(color.id);
    return (
      <button
        key={color.id}
        onClick={() => toggle(color.id)}
        title={color.art ? `${color.name} · ${color.art}` : color.name}
        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm"
        style={{
          background: active ? 'var(--otc-accent)' : 'var(--otc-surface)',
          color: active ? '#fff' : 'var(--otc-ink)',
          border: `1px solid ${active ? 'var(--otc-accent)' : 'var(--otc-line)'}`,
        }}
      >
        <Swatch color={color} size={14} />
        {color.name}
        {/*
          Артикул — мелко, рядом с названием, как в «Конструкциях → Цвета».

          По нему пластмассу заказывают у поставщика, а названия у разных
          производителей совпадают: «Pink» есть и у одного, и у другого.
          Прятать артикул в подсказку при наведении было мало — на телефоне
          наведения нет вовсе, и узнать его из карточки заказа было нельзя.
        */}
        {color.art && (
          <span
            className="tabular text-[11px]"
            style={{ color: active ? 'rgba(255,255,255,0.75)' : 'var(--otc-ink2)' }}
          >
            {color.art}
          </span>
        )}
      </button>
    );
  };

  const colorCategories = categories
    .filter((c) => c.kind === 'color')
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ru'));

  const service = colors.filter((c) => c.unspecified);
  const known = new Set(colorCategories.map((c) => c.id));
  const orphans = colors.filter((c) => !c.unspecified && (c.categoryId === null || !known.has(c.categoryId)));

  const section = (title: string, list: ApiColor[]) =>
    list.length === 0 ? null : (
      <div key={title} className="px-3 py-2">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-ink2">{title}</p>
        <div className="flex flex-wrap gap-1.5">{list.map(chip)}</div>
      </div>
    );

  return (
    <PickerField
      open={open}
      onToggle={() => setOpen((v) => !v)}
      onClose={() => setOpen(false)}
      placeholder="Выбрать цвет"
      /*
        В закрытом виде — сами образцы выбранных цветов, а не их число:
        цвет опознаётся глазом, и «выбрано 2» ничего не сказало бы.
      */
      summary={selected.map((id) => {
        const color = colors.find((c) => c.id === id);
        if (!color) return null;
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
            style={{ background: 'var(--otc-bg)', color: 'var(--otc-ink)' }}
          >
            <Swatch color={color} size={12} />
            {color.name}
            {color.art && <span className="tabular text-[10px] text-ink2">{color.art}</span>}
          </span>
        );
      })}
    >
      <div className="max-h-72 divide-y overflow-y-auto" style={{ borderColor: 'var(--otc-line)' }}>
        {/* Служебный «Цвет не указан» — наверху: он не относится ни к одной
            категории и нужен чаще остальных. */}
        {service.length > 0 && (
          <div className="px-3 py-2">
            <div className="flex flex-wrap gap-1.5">{service.map(chip)}</div>
          </div>
        )}
        {colorCategories.map((category) =>
          section(category.name, colors.filter((c) => !c.unspecified && c.categoryId === category.id)),
        )}
        {section('Без категории', orphans)}
      </div>
    </PickerField>
  );
}
